import { kindBadge, kindIsHoldOnly } from '../lib/phaseKinds';
import React, { useMemo, useState } from 'react';
import { useStore } from '../store/StoreContext';
import { useNav } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { format, parseISO, addDays } from 'date-fns';
import { IconPlus, IconGrid, IconCheck, IconChevronRight, IconX, IconTruck } from '../components/icons';
import JobModal from '../components/modals/JobModal';
import { Button, EntityCard, StatusPill, cardGridCls, PageHeader, Toolbar, SearchInput, EmptyState, pageWrapCls } from '../components/ui';

// ActiveMovesPage — operations triage (redesigned 2026-07-29, Jaylen).
//
// The dispatch board answers "who goes where"; Records answers "what did
// it bill". This page answers "what needs my attention RIGHT NOW":
//
//   In Progress      → crews on the clock this minute
//   Out Today        → today's jobs (amber flag if still only 'assigned' —
//                      it's move day and the crew hasn't been dispatched!)
//   Needs Recap      → dispatched/in-progress jobs whose date has PASSED
//                      but were never closed out (the recap-chase list)
//   This Week        → the next 7 days, assigned or dispatched
//   Unassigned       → booked work with no crew yet (stale-flagged if past)
//   Later            → beyond 7 days / dateless (collapsed by default)
//   Recently Done    → completed in the last 7 days, with a pointer to
//                      Records for everything older
//
// Every non-cancelled, non-walkthrough job lands in EXACTLY ONE section,
// so nothing is unreachable (QA M2 NEW-8 lesson). Walkthroughs are
// excluded — they're not moves.
//
// This page's JobModal is also the KEYBOARD-ACCESSIBLE fallback for the
// dispatch board's drag-and-drop (QA M3 OBS-6) — don't remove it.

// Mirrors PHASE_KIND_BADGE in DispatchBoard.jsx (which mirrors KIND_STYLES
// in PhasePlannerInline.jsx) so leg chips here look identical to the
// board's unassigned-rail pills. Keep the three in sync.
// Phase badges generated from segments (phaseKinds.js, 2026-08-26).

const BATCH_STATUSES = [
    { key: 'assigned', label: 'Assigned' },
    { key: 'dispatched', label: 'Dispatched' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'unassigned', label: 'Unassigned' },
];

// Button-styled checkbox — the native checkbox input renders
// inconsistently under the Tailwind CDN (see CLAUDE.md, PermCheckbox
// pattern). Not a form control (row-select for batch actions), so it
// stays a plain button rather than a CheckTile.
function SelectBox({ checked, onToggle }) {
    return (
        <button
            type="button"
            aria-pressed={checked}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 hover:border-blue-400'}`}
        >
            {checked && <IconCheck size={11} />}
        </button>
    );
}

function iso(d) { return format(d, 'yyyy-MM-dd'); }

function fmtDate(dateStr) {
    try { return format(parseISO(dateStr), 'EEE, MMM d'); } catch { return dateStr || 'No date'; }
}

function ActiveMovesPage() {
    const { clients, crew, trucks, jobs, saveJob, removeJob } = useStore();
    const { navigateToDispatch } = useNav();
    const [editJob, setEditJob] = useState(null);
    // When set, the JobModal opens with the office-recap panel expanded
    // and scrolled into view (deep-link from the recap checklist).
    const [editJobRecap, setEditJobRecap] = useState(false);
    const openRecapFor = (leg) => { setEditJobRecap(true); setEditJob(leg); };
    // Recap checklists are COLLAPSED to their "Recaps N/M" summary line by
    // default (Jaylen: cut the visual clutter) — expanded per card.
    const [openRecapLists, setOpenRecapLists] = useState(new Set());
    const toggleRecapList = (key) => {
        setOpenRecapLists(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };
    const { show: showToast } = useToast();
    const [selectedJobs, setSelectedJobs] = useState(new Set());
    const [search, setSearch] = useState('');
    const [showLater, setShowLater] = useState(false);

    const handleSave = async (form, deleteId) => {
        if (deleteId) { await removeJob(deleteId); showToast('Job deleted'); return; }
        await saveJob(form);
        showToast(form.id ? 'Job updated' : 'Job created');
    };

    const toggleSelect = (jobId) => {
        setSelectedJobs(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
            return next;
        });
    };

    const handleBatchStatus = async (newStatus) => {
        let count = 0;
        const previous = [];
        for (const jobId of selectedJobs) {
            const job = jobs.find(j => j.id === jobId);
            if (job) {
                previous.push({ ...job });
                await saveJob({ ...job, status: newStatus });
                count++;
            }
        }
        setSelectedJobs(new Set());
        // 1B: Undo restores each job's prior status from the snapshot we took.
        showToast(`${count} job${count !== 1 ? 's' : ''} updated to ${newStatus.replace('_', ' ')}`, undefined, {
            undo: async () => {
                try { for (const j of previous) await saveJob(j); showToast(`Restored ${previous.length} job${previous.length !== 1 ? 's' : ''}`); }
                catch (e) { showToast('Error: ' + e.message, 'error'); }
            },
        });
    };

    // ── Triage bucketing ────────────────────────────────────────────────
    const today = iso(new Date());
    const weekOut = iso(addDays(new Date(), 7));
    const recentCutoff = iso(addDays(new Date(), -7));

    const q = search.trim().toLowerCase();
    const matchesSearch = (job, client) => {
        if (!q) return true;
        return [client?.name, job.from_address, job.to_address, job.job_label, job.type]
            .some(v => (v || '').toLowerCase().includes(q));
    };

    const buckets = useMemo(() => {
        const b = { inprogress: [], todayJobs: [], needsRecap: [], week: [], unassigned: [], later: [], completedRecent: [] };
        for (const job of jobs || []) {
            if (job.type === 'WALKTHROUGH') continue;
            if (job.status === 'cancelled') continue;
            const client = clients.find(c => c.id === job.client_id);
            if (!matchesSearch(job, client)) continue;

            const start = job.move_date || null;
            const days = Math.max(1, Number(job.duration_days) || 1);
            let end = start;
            if (start && days > 1) {
                try { end = iso(addDays(parseISO(start), days - 1)); } catch { end = start; }
            }

            if (job.status === 'in_progress') { b.inprogress.push(job); continue; }
            if (job.status === 'completed') {
                if (end && end >= recentCutoff) b.completedRecent.push(job);
                continue; // older completed live in Records
            }
            if (job.status === 'unassigned') { b.unassigned.push(job); continue; }
            // assigned / dispatched:
            if (!start) { b.later.push(job); continue; }
            if (end < today) { b.needsRecap.push(job); continue; }
            if (start <= today && today <= end) { b.todayJobs.push(job); continue; }
            if (start <= weekOut) { b.week.push(job); continue; }
            b.later.push(job);
        }
        return b;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs, clients, q, today]);

    const totalVisible = Object.values(buckets).reduce((s, arr) => s + arr.length, 0);

    // Header summary line (page-headers.md): live counts over ALL jobs
    // (not the search-filtered buckets), so it doesn't flicker as you type.
    const summary = useMemo(() => {
        let active = 0, inProgress = 0, outToday = 0, needsRecap = 0;
        for (const job of jobs || []) {
            if (job.type === 'WALKTHROUGH' || job.status === 'cancelled' || job.status === 'completed') continue;
            active++;
            if (job.status === 'in_progress') { inProgress++; continue; }
            if (job.status === 'unassigned') continue;
            const start = job.move_date || null;
            if (!start) continue;
            const days = Math.max(1, Number(job.duration_days) || 1);
            let end = start;
            if (days > 1) { try { end = iso(addDays(parseISO(start), days - 1)); } catch { end = start; } }
            if (end < today) needsRecap++;
            else if (start <= today && today <= end) outToday++;
        }
        return `${active} active job${active === 1 ? '' : 's'} · ${inProgress} in progress · ${outToday} out today · ${needsRecap} need${needsRecap === 1 ? 's' : ''} recap`;
    }, [jobs, today]);

    // ── Multi-phase grouping ────────────────────────────────────────────
    // A long-distance / multi-day estimate materializes as one move_jobs
    // row PER phase (Load / Drive / Unload / per-day legs) — Kay Siebold's
    // 6-leg run rendered as 6 sibling cards (Jaylen 2026-07-29). Within a
    // section, legs sharing an estimate collapse into ONE card with the
    // date range + phase count, expandable to the individual legs. Legs of
    // the same estimate in DIFFERENT sections stay separate on purpose —
    // that split is the triage signal (e.g. 2 legs done, 1 leg today).
    const groupJobs = (sectionJobs) => {
        const out = [];
        const byEstimate = new Map();
        for (const job of sectionJobs) {
            const key = job.estimate_id || `solo:${job.id}`;
            if (byEstimate.has(key)) { byEstimate.get(key).legs.push(job); }
            else { const g = { key, legs: [job] }; byEstimate.set(key, g); out.push(g); }
        }
        return out;
    };


    const renderCard = (job) => {
        const client = clients.find(c => c.id === job.client_id);
        const leader = crew.find(c => c.id === job.leader_id);
        const isStaleUnassigned = job.status === 'unassigned' && job.move_date && job.move_date < today;
        // Ghost holds (parked loaded trucks) never dispatch — no flag.
        const notDispatchedToday = job.status === 'assigned' && job.move_date && job.move_date <= today && !kindIsHoldOnly(job.phase_kind);
        // Status is implied by the section; the pill only flags exceptions.
        const badge = notDispatchedToday ? <StatusPill label="Not dispatched yet" color="#d97706" />
            : isStaleUnassigned ? <StatusPill label="Past date" color="#e11d48" />
            : null;
        const facts = [
            { label: 'Type', value: job.job_label || job.type },
            { label: 'Date', value: `${fmtDate(job.move_date)}${job.time ? ` · ${job.time}` : ''}${Number(job.duration_days) > 1 ? ` · ${job.duration_days} days` : ''}` },
            { label: 'Crew', value: `${(job.crew_ids || []).length ? `${job.crew_ids.length} crew` : `${job.crew_size || 0} planned`}${job.duration ? ` • ${job.duration}h` : ''}` },
        ];
        if (leader) facts.push({ label: 'Lead', value: <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: leader.display_color }}></span>{leader.name}</span> });
        return (
            // One card recipe — lists.md §4 (EntityCard owns padding, radius, hover, selected ring).
            <EntityCard
                key={job.id}
                avatar={<SelectBox checked={selectedJobs.has(job.id)} onToggle={() => toggleSelect(job.id)} />}
                title={client?.name || 'Unknown'}
                subtitle={`${job.from_address || '—'} → ${job.to_address || '—'}`}
                badge={badge}
                facts={facts}
                selected={selectedJobs.has(job.id)}
                onClick={() => setEditJob(job)}
                footer={job.move_date ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        full
                        leftIcon={<IconGrid size={12}/>}
                        onClick={(e) => { e.stopPropagation(); navigateToDispatch(job.move_date); }}
                    >
                        View on Dispatch Board
                    </Button>
                ) : null}
            >
                {renderRecapChecklist([job], job.id)}
            </EntityCard>
        );
    };

    // Recap checklist rows for a set of legs: only DUE legs count (a leg
    // whose date hasn't arrived doesn't owe a recap yet). ✓ = recap in,
    // ○ = missing with a one-click jump into the office-recap panel.
    const legEnd = (leg) => {
        if (!leg.move_date) return null;
        const days = Math.max(1, Number(leg.duration_days) || 1);
        try { return days > 1 ? iso(addDays(parseISO(leg.move_date), days - 1)) : leg.move_date; } catch { return leg.move_date; }
    };
    const renderRecapChecklist = (legs, listKey) => {
        const due = legs.filter(l => {
            const end = legEnd(l);
            if (!end || end > today) return false;
            if (l.status === 'cancelled' || l.status === 'unassigned') return false;
            // Completed WITHOUT a recap = deliberately closed with no data
            // (e.g. batch-completing pre-launch history) — don't nag.
            if (l.status === 'completed' && !l.recap_submitted_at) return false;
            return true;
        });
        if (!due.length) return null;
        const missing = due.filter(l => !l.recap_submitted_at);
        if (!missing.length) return null;
        const isOpen = openRecapLists.has(listKey);
        return (
            <div className="border-t border-slate-100 pt-2">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleRecapList(listKey); }}
                    className="w-full flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                >
                    <IconChevronRight size={11} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}/>
                    <span>Recaps {due.length - missing.length}/{due.length} completed</span>
                    <span className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0"/>
                </button>
                <div className={`space-y-1 mt-1.5 ${isOpen ? '' : 'hidden'}`}>
                    {due.map((leg, i) => {
                        const badge = leg.phase_kind ? kindBadge(leg.phase_kind) : null;
                        const label = badge?.label || (legs.length > 1 ? `DAY ${legs.indexOf(leg) + 1}` : 'JOB');
                        const has = !!leg.recap_submitted_at;
                        let shortDate = '';
                        try { shortDate = leg.move_date ? format(parseISO(leg.move_date), 'M/d') : ''; } catch { shortDate = ''; }
                        return (
                            <div key={leg.id} className="flex items-center gap-2 text-xs">
                                {/* Status indicator: solid green check = recap submitted,
                                    solid red X = still missing (Jaylen: read as a status
                                    light, not an empty checkbox). */}
                                <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-white ${has ? 'bg-emerald-500' : 'bg-rose-500'}`}>
                                    {has ? <IconCheck size={10} /> : <IconX size={9} />}
                                </span>
                                <span className={`font-semibold ${has ? 'text-slate-400' : 'text-slate-700'}`}>{label} {shortDate}</span>
                                {has ? (
                                    <span className="text-[10px] font-medium text-emerald-600">{leg.recap_source === 'office' ? 'office entry' : 'submitted'}</span>
                                ) : (
                                    <>
                                        <span className="text-[10px] font-medium text-rose-500">no recap submitted</span>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            className="ml-auto"
                                            onClick={(e) => { e.stopPropagation(); openRecapFor(leg); }}
                                        >
                                            Manual Recap Entry
                                        </Button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // One card for a multi-leg estimate: date range + leg count, expandable
    // to per-leg rows (each with its own select, edit, flags, board link).
    const renderGroupCard = (group) => {
        const legs = group.legs;
        if (legs.length === 1) return renderCard(legs[0]);
        const first = legs[0];
        const last = legs[legs.length - 1];
        const client = clients.find(c => c.id === first.client_id);
        const leadNames = [...new Set(legs.map(l => crew.find(c => c.id === l.leader_id)?.name).filter(Boolean))];
        const allSelected = legs.every(l => selectedJobs.has(l.id));
        const anyNotDispatched = legs.some(l => l.status === 'assigned' && l.move_date && l.move_date <= today && !kindIsHoldOnly(l.phase_kind));
        const facts = [
            { label: 'Type', value: `${first.job_label || first.type} · ${legs.length} legs` },
            { label: 'Dates', value: `${fmtDate(first.move_date)} → ${fmtDate(last.move_date)}` },
            { label: 'Crew', value: (first.crew_ids || []).length ? `${first.crew_ids.length} crew` : `${first.crew_size || 0} planned` },
        ];
        if (leadNames.length > 0) facts.push({ label: 'Lead', value: leadNames.join(', ') });
        return (
            // One card recipe — lists.md §4 (EntityCard owns padding, radius, hover, selected ring).
            <EntityCard
                key={group.key}
                avatar={<SelectBox checked={allSelected} onToggle={() => {
                    setSelectedJobs(prev => {
                        const next = new Set(prev);
                        for (const l of legs) { if (allSelected) next.delete(l.id); else next.add(l.id); }
                        return next;
                    });
                }} />}
                title={client?.name || 'Unknown'}
                subtitle={`${first.from_address || '—'} → ${last.to_address || first.to_address || '—'}`}
                badge={anyNotDispatched ? <StatusPill label="Not dispatched yet" color="#d97706" /> : null}
                facts={facts}
                selected={allSelected}
            >
                {/* Leg chips — same palette as the dispatch board's
                    unassigned-rail pills. Always visible (Jaylen:
                    use the empty card bottom, no collapse). Click a
                    chip to open that leg's JobModal. */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    {legs.map((leg, i) => {
                        const badge = leg.phase_kind ? kindBadge(leg.phase_kind) : { bg: 'rgba(203,213,225,0.9)', fg: '#334155', label: `DAY ${i + 1}` };
                        const legDue = leg.status === 'assigned' && leg.move_date && leg.move_date <= today && !kindIsHoldOnly(leg.phase_kind);
                        const legDone = leg.status === 'completed';
                        let shortDate = '';
                        try { shortDate = leg.move_date ? format(parseISO(leg.move_date), 'M/d') : ''; } catch { shortDate = ''; }
                        return (
                            <button
                                key={leg.id}
                                onClick={(e) => { e.stopPropagation(); setEditJob(leg); }}
                                title={`${fmtDate(leg.move_date)}${leg.time ? ' · ' + leg.time : ''} · ${leg.status.replace('_', ' ')} — click to edit`}
                                className={`px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide hover:brightness-95 ${legDone ? 'opacity-50' : ''} ${legDue ? 'ring-1 ring-amber-500' : ''}`}
                                style={{ backgroundColor: badge.bg, color: badge.fg }}
                            >
                                {badge.label}{shortDate ? ` ${shortDate}` : ''}
                            </button>
                        );
                    })}
                </div>
                {renderRecapChecklist(legs, group.key)}
            </EntityCard>
        );
    };

    const Section = ({ title, jobs: sectionJobs, tone = 'text-slate-900', badge = 'bg-slate-100 text-slate-600', subtitle }) => {
        if (!sectionJobs.length) return null;
        const groups = groupJobs(sectionJobs);
        return (
            <div>
                <h2 className={`text-lg font-bold mb-1 flex items-center gap-2 ${tone}`}>
                    {title}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${badge}`}>{groups.length}</span>
                </h2>
                {subtitle && <p className="text-xs text-slate-400 mb-3">{subtitle}</p>}
                {!subtitle && <div className="mb-3"/>}
                <div className={cardGridCls}>
                    {groups.map(g => renderGroupCard(g))}
                </div>
            </div>
        );
    };

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Active Moves"
                summary={summary}
                help={<>
                    <p>This is the operations triage list — <span className="font-semibold">what needs attention right now</span>, sorted by urgency: <span className="text-emerald-600 font-semibold">In Progress</span> (crews on the clock) → <span className="text-blue-600 font-semibold">Out Today</span> (an amber pill means it's move day and the crew hasn't been dispatched) → <span className="text-rose-600 font-semibold">Needs Recap</span> (date passed, never closed out) → This Week → <span className="text-amber-600 font-semibold">Unassigned</span> → Later (collapsed) → Recently Done.</p>
                    <p>A multi-day move shows as <span className="font-semibold">one card</span> with a colored chip per leg (PACK, LOAD, DRIVE...) — click a chip to edit that leg. The <span className="font-semibold">Recaps N/M</span> line expands to show which legs have recaps: green check = submitted, red X = missing, and <span className="font-semibold">Manual Recap Entry</span> lets the office enter one when a lead didn't (hours, payment, tips — it completes the job).</p>
                    <p>Checkboxes select jobs (a group's box selects all its legs) for <span className="font-semibold">batch status changes</span>. Completed jobs older than 7 days live in Records.</p>
                    <p className="text-xs text-slate-400">This page is also the keyboard-friendly way to edit any job the dispatch board handles by drag-and-drop.</p>
                </>}
                actions={<Button variant="primary" leftIcon={<IconPlus size={16}/>} onClick={() => setEditJob('new')}>New Job</Button>}
            />

            {/* Search + batch actions — one toolbar card (page-headers.md).
                No status chips: the sections ARE the status filter here. */}
            <Toolbar>
                <SearchInput value={search} onChange={setSearch} placeholder="Search client, address..." />
                {selectedJobs.size > 0 && (
                    <div className="ml-auto flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200 flex-wrap">
                        <span className="text-sm text-blue-700 font-medium">{selectedJobs.size} selected →</span>
                        {BATCH_STATUSES.map(s => (
                            <Button key={s.key} variant="secondary" size="sm" onClick={() => handleBatchStatus(s.key)}>
                                {s.label}
                            </Button>
                        ))}
                        <Button variant="tertiary" size="sm" onClick={() => setSelectedJobs(new Set())}>Clear</Button>
                    </div>
                )}
            </Toolbar>

            <Section title="In Progress" jobs={buckets.inprogress} tone="text-emerald-700" badge="bg-emerald-100 text-emerald-700" subtitle="Crews on the clock right now." />
            <Section title="Out Today" jobs={buckets.todayJobs} tone="text-slate-900" badge="bg-blue-100 text-blue-700" subtitle="Today's moves — amber flag means it's move day and the crew hasn't been dispatched." />
            <Section title="Needs Recap" jobs={buckets.needsRecap} tone="text-rose-700" badge="bg-rose-100 text-rose-700" subtitle="The date passed but the job was never closed out — chase the lead's recap or complete it manually." />
            <Section title="This Week" jobs={buckets.week} subtitle="Assigned or dispatched in the next 7 days." />
            <Section title="Unassigned" jobs={buckets.unassigned} tone="text-amber-700" badge="bg-amber-100 text-amber-700" subtitle="Booked work with no crew on it yet." />

            {buckets.later.length > 0 && (
                <div>
                    <button onClick={() => setShowLater(v => !v)} className="text-lg font-bold text-slate-500 hover:text-slate-700 flex items-center gap-2">
                        <IconChevronRight size={16} className={`transition-transform ${showLater ? 'rotate-90' : ''}`}/>
                        Later
                        <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{buckets.later.length}</span>
                        <span className="text-xs font-normal text-slate-400">beyond 7 days / no date</span>
                    </button>
                    {showLater && (
                        <div className={`${cardGridCls} mt-3`}>
                            {groupJobs(buckets.later).map(g => renderGroupCard(g))}
                        </div>
                    )}
                </div>
            )}

            {buckets.completedRecent.length > 0 && (
                <div>
                    <h2 className="text-lg font-bold text-slate-400 mb-1 flex items-center gap-2">
                        Recently Done
                        <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full">{buckets.completedRecent.length}</span>
                    </h2>
                    <p className="text-xs text-slate-400 mb-3">
                        Completed in the last 7 days — older jobs live in{' '}
                        <button className="text-blue-600 hover:underline font-medium" onClick={() => { window.location.hash = 'records'; }}>Records</button>.
                    </p>
                    <div className={cardGridCls}>
                        {groupJobs(buckets.completedRecent).map(g => renderGroupCard(g))}
                    </div>
                </div>
            )}

            {totalVisible === 0 && (
                q ? (
                    <EmptyState
                        icon={<IconTruck size={28}/>}
                        title="No moves match"
                        hint="Nothing matches this search. Clear it to see every active and upcoming move."
                        action={<Button variant="tertiary" size="sm" onClick={() => setSearch('')}>Clear filters</Button>}
                    />
                ) : (
                    <EmptyState
                        icon={<IconTruck size={28}/>}
                        title="Nothing needs attention"
                        hint="No active or upcoming moves. Booked estimates land here automatically, or add a one-off job."
                        action={<Button size="sm" leftIcon={<IconPlus size={16}/>} onClick={() => setEditJob('new')}>New Job</Button>}
                    />
                )
            )}

            {editJob && <JobModal job={editJob === 'new' ? null : editJob} autoOpenRecap={editJobRecap} onClose={() => { setEditJob(null); setEditJobRecap(false); }} onSave={async (form, deleteId) => { await handleSave(form, deleteId); setEditJob(null); setEditJobRecap(false); }} clients={clients} crew={crew} trucks={trucks} jobs={jobs} />}
        </div>
    );
}

export default ActiveMovesPage;
